import * as React from "react";
import { connect } from "react-redux";
import { Grid, Modal, Form } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";

import { validators } from "not-valid";

import { PartialOhCaseListModel } from "../model";
import { ApplicationState } from "../../../applicationState";
import { routeIdSelector as employeeRecordIdSelector } from "../../employeeRecord/selectors";
import { customerSelector } from "../../../crud/customer/selectors";
import { AddUnknownEmployeeModel, emptyAddUnknownEmployeeModel, MatchingEmployeeModel } from "../../../calendar/clinics/models";
import { WorkGroup, loadWorkGroups } from "../../../crud/workGroup";
import { Dispatch } from "../../../store";
import {
    searchForEmployeeForCustomer,
    matchOhCaseToExistingEmployee,
    matchOhCaseToNewEmployee
} from "../actions";

interface OwnProps {
    ohCase: PartialOhCaseListModel;
    onClose: () => void;
}

interface StateProps {
    path: string;
    workGroupOptions: WorkGroup[];
    customerId: string;
    employeeRecordId: string;
}

interface DispatchProps {
    loadWorkGroups: (customerId: string) => Promise<void>;
    searchForEmployee: (customerId: string, employeeName: string) => Promise<MatchingEmployeeModel[]>;
    matchOhCaseToExistingEmployee: (customerId: string, ohCaseId: string, employeeRecordId: string) => Promise<void[]>;
    matchOhCaseToNewEmployee: (customerId: string, ohCaseId: string, employeeModel: AddUnknownEmployeeModel) => Promise<void[]>;
}

export const MatchEmployeeModalUnconnected: React.FunctionComponent<StateProps & OwnProps & DispatchProps> = (props: StateProps & OwnProps & DispatchProps) => {

    const saveRequestNames: string[] = [];
    const [addingNew, setAddingNew] = React.useState<boolean>(false);
    const [showErrors, setShowErrors] = React.useState<boolean>(false);
    const [matchedEmployees, setMatchedEmployees] = React.useState<MatchingEmployeeModel[]>([]);
    const [employeeName, setEmployeeName] = React.useState<string>(null);
    const [unknownEmployeeModel, setUnknownEmployeeModel] = React.useState<AddUnknownEmployeeModel>(emptyAddUnknownEmployeeModel());

    React.useEffect(() => {
        if (props.customerId && props.employeeRecordId) {
            props.loadWorkGroups(props.customerId);
        }
    }, [props.customerId, props.employeeRecordId]);

    const onCancel = () => {
        setAddingNew(false);
        setMatchedEmployees([]);
        setEmployeeName(null);
        setShowErrors(false);
        setUnknownEmployeeModel(emptyAddUnknownEmployeeModel());
        props.onClose();
    };

    const onMatch = () => {
        setShowErrors(true);

        if (addingNew) {
            if (!unknownEmployeeModel.title
                || !unknownEmployeeModel.firstName
                || !unknownEmployeeModel.lastName
                || !unknownEmployeeModel.gender
                || !unknownEmployeeModel.dateOfBirth
                || !unknownEmployeeModel.postcode
                || !unknownEmployeeModel.jobTitle
                || (!!unknownEmployeeModel.dateOfBirth && !unknownEmployeeModel.dateOfBirth.isValid())) {
                return;
            }

            props.matchOhCaseToNewEmployee(props.customerId, props.ohCase.id, unknownEmployeeModel);
        }
        else {
            if (!unknownEmployeeModel.matchingEmployee) {
                return;
            }

            props.matchOhCaseToExistingEmployee(props.customerId, props.ohCase.id, unknownEmployeeModel.matchingEmployee);
        }
    };

    const searchForEmployee = () => {
        props.searchForEmployee(props.customerId, employeeName)
            .then(employees => setMatchedEmployees(employees.filter(e => e.id !== props.ohCase.employeeRecordId)));
    };

    const onChangeEmployeeName = (newEmployeeName: string) => {
        setUnknownEmployeeModel({ ...unknownEmployeeModel, employeeName: newEmployeeName });
        setEmployeeName(newEmployeeName);
    };

    const onChangeTitle = (title: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, title });

    const onChangeFirstName = (firstName: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, firstName });

    const onChangeLastName = (lastName: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, lastName });

    const onChangeSecondaryReference = (secondaryReference: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, secondaryReference });

    const onChangeGender = (gender: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, gender });

    const onChangeEmailAddress = (emailAddress: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, emailAddress });

    const onChangeJobTitle = (jobTitle: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, jobTitle });

    const onChangeDateOfBirth = (dateOfBirth: moment.Moment) => setUnknownEmployeeModel({ ...unknownEmployeeModel, dateOfBirth });

    const onChangeAddressLine1 = (addressLine1: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, addressLine1 });

    const onChangePostcode = (postcode: string) => setUnknownEmployeeModel({ ...unknownEmployeeModel, postcode });

    const onChangeWorkGroups = (newWorkGroups: string[]) => setUnknownEmployeeModel({ ...unknownEmployeeModel, workGroups: newWorkGroups });

    const onChangeMatchingEmployee = (employeeId: any) => {
        if (employeeId) {
            const matchedEmployeeWorkGroups = matchedEmployees.find(e => e.id === employeeId)?.workGroups || [];
            setUnknownEmployeeModel({ ...unknownEmployeeModel, matchingEmployee: employeeId, workGroups: matchedEmployeeWorkGroups });
        } else {
            setUnknownEmployeeModel({ ...unknownEmployeeModel, matchingEmployee: employeeId });
        }
    };

    const matchingEmployees = matchedEmployees.map(e => (
        {
            text: `${e.firstName} ${e.lastName} - ${e.dateOfBirth} - ${e.personalReferenceNumber}`,
            value: e.id,
            workGroups: e.workGroups
        }
    ));

    return (
        <>
            <Modal.Header content="Match Employee" />
            <Modal.Content>
                <Form>
                    {!addingNew ? (
                        <>
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width="12">
                                        <Input.Text
                                            key={"employeeName"}
                                            label={"Search Employee Name"}
                                            value={employeeName}
                                            showErrors={showErrors}
                                            onChange={onChangeEmployeeName}
                                        />
                                    </Grid.Column>
                                    <Grid.Column width="4">
                                        <IsLoadingButton
                                            requestNames={saveRequestNames}
                                            onClick={searchForEmployee}
                                            content="Search"
                                            className="centered-button"
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                            {matchedEmployees?.length > 0 ? (
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column>
                                            <Input.Dropdown
                                                key={"matchingEmployee"}
                                                required
                                                value={unknownEmployeeModel.matchingEmployee}
                                                showErrors={showErrors}
                                                label="Matching employee"
                                                options={matchingEmployees}
                                                dynamicOptions
                                                onChange={onChangeMatchingEmployee}
                                            />
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row>
                                        <Grid.Column width="12">
                                            <p>If there isn't matching employee try searching again</p>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            ) : (
                                <Grid>
                                    <Grid.Row>
                                        <Grid.Column width="12">
                                            <p>No employees with provided employee name found. try Searching again</p>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>
                            )}
                        </>
                    ) : (
                        <>
                            <Input.Text
                                key={"secondaryReference"}
                                value={unknownEmployeeModel.secondaryReference}
                                showErrors={showErrors}
                                label="Employee Unique ID"
                                onChange={onChangeSecondaryReference}
                                validation={[ validators.validLength({ max: 20 }) ]}
                            />
                            <Input.Text
                                key={"title"}
                                required
                                value={unknownEmployeeModel.title}
                                showErrors={showErrors}
                                label="Title"
                                onChange={onChangeTitle}
                            />
                            <Input.Text
                                key={"firstName"}
                                required
                                value={unknownEmployeeModel.firstName}
                                showErrors={showErrors}
                                label="First Name"
                                onChange={onChangeFirstName}
                            />
                            <Input.Text
                                key={"lastName"}
                                required
                                value={unknownEmployeeModel.lastName}
                                showErrors={showErrors}
                                label="Last Name"
                                onChange={onChangeLastName}
                            />
                            <Input.Dropdown
                                key={"gender"}
                                required
                                placeholder="Please select"
                                value={unknownEmployeeModel.gender}
                                showErrors={showErrors}
                                options={GENDER_OPTIONS}
                                label="Gender"
                                onChange={onChangeGender}
                            />
                            <Input.Date
                                key={"dateOfBirth"}
                                required
                                label={"Date Of Birth"}
                                value={unknownEmployeeModel.dateOfBirth}
                                showErrors={showErrors}
                                onChange={onChangeDateOfBirth}
                            />
                            <Input.Text
                                key={"emailAddress"}
                                value={unknownEmployeeModel.emailAddress}
                                showErrors={showErrors}
                                label="Email Address"
                                onChange={onChangeEmailAddress}
                            />
                            <Input.Text
                                key={"addressLine1"}
                                value={unknownEmployeeModel.addressLine1}
                                showErrors={showErrors}
                                label="Address Line 1"
                                onChange={onChangeAddressLine1}
                            />
                            <Input.Text
                                key={"postcode"}
                                required
                                value={unknownEmployeeModel.postcode}
                                showErrors={showErrors}
                                label="Postcode"
                                onChange={onChangePostcode}
                            />
                            <Input.Text
                                key={"jobTitle"}
                                required
                                value={unknownEmployeeModel.jobTitle}
                                showErrors={showErrors}
                                label="Job Title"
                                onChange={onChangeJobTitle}
                            />
                            <Input.DropdownMulti
                                key={"workGroups"}
                                value={unknownEmployeeModel.workGroups}
                                showErrors={showErrors}
                                label="Work Groups"
                                options={props.workGroupOptions && props.workGroupOptions.map(g => ({ text: g.name, value: g.id }))}
                                onChange={onChangeWorkGroups}
                            />
                        </>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <IsLoadingButton
                    requestNames={saveRequestNames}
                    onClick={onCancel}
                    content="Cancel"
                />
                <IsLoadingButton
                    requestNames={saveRequestNames}
                    onClick={onMatch}
                    content={addingNew ? "Create Employee and Match" : "Match"}
                />
            </Modal.Actions>
        </>
    );
};

export function mapStateToProps(state: ApplicationState): StateProps {
    const customerId = customerSelector(state).id;
    const employeeRecordId = employeeRecordIdSelector(state);

    return {
        path: state.router.pathname,
        workGroupOptions: state.workGroups.filter(w => w.customerId === customerId),
        customerId,
        employeeRecordId
    };
}

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    loadWorkGroups: (customerId) => dispatch<Promise<void>>(loadWorkGroups({ customerId })),
    searchForEmployee: (customerId, employeeName) => dispatch<Promise<any>>(searchForEmployeeForCustomer(customerId, employeeName)),
    matchOhCaseToExistingEmployee: (customerId: string, ohCaseId: string, employeeRecordId: string) =>
        dispatch<Promise<any>>(matchOhCaseToExistingEmployee(customerId, ohCaseId, employeeRecordId)),
    matchOhCaseToNewEmployee: (customerId: string, ohCaseId: string, employeeModel: AddUnknownEmployeeModel) =>
        dispatch<Promise<any>>(matchOhCaseToNewEmployee(customerId, ohCaseId, employeeModel))
});

export const MatchEmployeeModal = connect(mapStateToProps, mapDispatchToProps)(MatchEmployeeModalUnconnected);
